import React from 'react';
import PropTypes from 'prop-types';
import { Viewer } from '../pacifa/pacifa3d-sdk.1.2.0.es';
import { Overlay } from '../common/common';
import { useTranslation } from 'react-i18next';
import { detectSignificantSizeChange } from '../utils/detectSignificantSizeChange';

export default function SeatMapPacifa({
    zone = {},
    pacifaKey,
    onSeatClicked = (seat) => {},
    onSeatOver = (seat, { element }) => {},
    onHidePopup = () => {},
    idFrame,
    onFrame = (iframeEl) => {},
    overlay = null,
}) {
    const { t } = useTranslation();

    React.useEffect(() => {
        const viewer = new Viewer(idFrame + '-container', pacifaKey, {
            requireCtrlOrMetaKeyToZoom: true,
            showZoomButtons: true,
            recenterOnWidowResize: false,
            requireCtrlOrMetaKeyToZoomWarning: t('requireCtrlOrMetaKeyToZoomWarning'),
        });
        viewer
            .displayBlockView(zone.pacifa_id, {
                seatIds: zone.seats.map((s) => s.pacifa_id),
                seatsStyles: zone.seats.map((s) => ({
                    id: s.pacifa_id,
                    css: s.style?.out,
                    cssHover: s.style?.over,
                })),
            })
            .then(() => {
                const iframe = viewer.containerEl.querySelector('iframe');
                iframe.id = idFrame;

                // Slightly hacky way to hide the "ctrl+wheel" message if you click it
                iframe.contentDocument.addEventListener('click', e => {
                    const warnMessage=iframe.contentDocument.getElementById('requireCtrlOrMetaKeyToZoomWarning');
                    if(warnMessage?.style.opacity !== '0') {
                        warnMessage.style.display='none';
                        try{
                            window.sessionStorage.setItem('pacifa-knowsHowToZoomOnMap', 'yes');
                        }catch (e) {
                            console.warn(e);
                        }
                    }
                });
                onFrame(iframe);
            });
        viewer.on('seatClicked', ({ seatId }) => {
            onSeatClicked(zone.seats.find((seat) => seat.pacifa_id === seatId));
        });
        let hovering = null;
        viewer.on('seatMouseMove', ({ seatId }, element) => {
            if (hovering === seatId) return;
            hovering = seatId;
            onSeatOver(
                zone.seats.find((seat) => seat.pacifa_id === seatId),
                {
                    element: viewer.containerEl
                        .querySelector('iframe')
                        ?.contentDocument.querySelector(`polygon[data-seat="${seatId}"]`),
                },
            );
        });
        viewer.on('seatMouseLeave', (seatId) => {
            onHidePopup();
        });

        // After menhir changes the size of the iframe , we re-center the pacifa map
        return detectSignificantSizeChange(viewer.containerEl, () =>
            viewer.updateOnResize(),
        );
    }, [zone.pacifa_id, pacifaKey, idFrame]);
    return (
        <div id={idFrame + '-container'} className="frame-container">
            {overlay && <Overlay className={`${idFrame}-overlay`}>{overlay}</Overlay>}
        </div>
    );
}

SeatMapPacifa.propTypes = {
    /** The zone object (pacifa block) containing seats. */
    zone: PropTypes.object,
    /** The main project id */
    pacifaKey: PropTypes.string.isRequired,
    /** Called with the zone.seats object that was clicked */
    onSeatClicked: PropTypes.func,
    /** Called with the zone.seats object that was hovered */
    onSeatOver: PropTypes.func,
    /** Called anytime the mouse leaves a seat*/
    onHidePopup: PropTypes.func,
    /** Id for the iframe */
    idFrame: PropTypes.string,
    onFrame: PropTypes.func,
    overlay: PropTypes.node,
};
